import React, { useState } from 'react'
import { StyleSheet, View, FlatList, RefreshControl } from 'react-native'
import { Text, Icon } from 'react-native-paper'
import { StackScreenProps } from '@react-navigation/stack'
import { AppNavigatorParams } from '../../../navigation/types'
import { useTranslation } from 'react-i18next'
import { useListInvitationsQuery } from '../../../generated/graphql'
import { ScreenWrapper } from '../../../@ui/containers/grid/screen.wrapper'
import { CreateInvitationButton } from './invitation/create-invitation.button'
import { FilterChips, FilterOption } from '../../../@ui/buttons/filter.chips'
import { InvitationCard } from './invitation/invitation.card'
import {
  SortOptions,
  SortField,
  SortDirection,
} from '../../../@ui/buttons/sort.button'
import { TouchableIconWithToolTip } from '../../../utils/meldd-tooltip'
import { useTheme } from '../../../hooks/theme.hook'
import { Row } from '../../../@ui'

type InvitationStatus = 'active' | 'used' | 'all'

function InvitationLinksScreen({
  route,
  navigation,
}: StackScreenProps<AppNavigatorParams, 'InvitationLinks'>) {
  const { nodeId } = route.params
  const { t } = useTranslation('nodes')
  const theme = useTheme()
  const [statusFilter, setStatusFilter] = useState<InvitationStatus>('active')
  const [sortField, setSortField] = useState<SortField>('createdAt')
  const [sortDirection, setSortDirection] = useState<SortDirection>('DESC')

  const filterOptions: FilterOption<InvitationStatus>[] = [
    { value: 'active', label: t('active') },
    { value: 'used', label: t('used') },
    { value: 'all', label: t('all') },
  ]

  const sortOptions = [
    {
      field: 'createdAt',
      direction: 'DESC' as const,
      label: t('sort-recent-first'),
      icon: 'sort-clock-descending-outline',
    },
    {
      field: 'createdAt',
      direction: 'ASC' as const,
      label: t('sort-oldest-first'),
      icon: 'sort-clock-ascending-outline',
    },
    {
      field: 'usage',
      direction: 'DESC' as const,
      label: t('sort-most-used-first'),
      icon: 'sort-numeric-descending',
    },
    {
      field: 'usage',
      direction: 'ASC' as const,
      label: t('sort-least-used-first'),
      icon: 'sort-numeric-ascending',
    },
  ]

  const { data, loading, refetch } = useListInvitationsQuery({
    variables: {
      input: {
        nodeId,
        status: statusFilter,
        limit: 50,
        offset: 0,
        orderBy: sortField,
        orderDirection: sortDirection,
      },
    },
  })

  const handleSortChange = (field: SortField, direction: SortDirection) => {
    setSortField(field)
    setSortDirection(direction)
  }

  const invitations = data?.listInvitations?.invitations || []
  const total = data?.listInvitations?.total || 0

  return (
    <ScreenWrapper>
      <View style={styles.container}>
        <Row justifyContent="flex-end">
          <CreateInvitationButton nodeId={nodeId} onCreated={refetch} />
        </Row>
        <View style={styles.topBar}>
          <View>
            <FilterChips
              options={filterOptions}
              selectedValue={statusFilter}
              onValueChange={setStatusFilter}
            />
            <Text variant="bodyMedium" style={styles.totalText}>
              {t('total-invitations')}: {total}
            </Text>
          </View>

          <View style={styles.topBarActions}>
            <SortOptions
              options={sortOptions}
              selectedField={sortField}
              selectedDirection={sortDirection}
              onSortChange={handleSortChange}
            />
            <TouchableIconWithToolTip
              icon={
                <Icon
                  source="account-cog"
                  size={24}
                  color={theme.colors.onSurface}
                />
              }
              tooltip={t('manage-members')}
              onPress={() => navigation.navigate('NodeMembers', { nodeId })}
            />
          </View>
        </View>

        <FlatList
          data={invitations}
          renderItem={({ item }) => (
            <InvitationCard invitation={item} onDeleted={refetch} />
          )}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.list}
          refreshControl={
            <RefreshControl refreshing={loading} onRefresh={refetch} />
          }
          ListEmptyComponent={
            <View style={styles.empty}>
              <Text variant="bodyLarge" style={styles.emptyText}>
                {t('no-invitations-found')}
              </Text>
            </View>
          }
        />
      </View>
    </ScreenWrapper>
  )
}

export default InvitationLinksScreen

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  topBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: 8,
    columnGap: 8,
  },
  topBarActions: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'flex-end',
    flexGrow: 1,
    flexShrink: 1,
  },
  totalText: {
    opacity: 0.7,
    marginTop: 2,
    marginBottom: 16,
  },
  list: {
    paddingBottom: 16,
  },
  empty: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    gap: 16,
  },
  emptyText: {
    opacity: 0.6,
  },
})
